import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import AppLeftSideBar from "../../components/SideBar/AppLeftSideBar";
import Button from "../../components/FormComponents/Button";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";
import { buildPath } from "../../routes/routesPath";
import { getAuditTrace } from "../../services/apis/auditApis";
import { buildReportText, isUnknownTrace } from "./utils";
import useSubjectConfig from "./hooks/useSubjectConfig";
import TraceHeaderCard from "./components/TraceHeaderCard";
import TraceTimeline from "./components/TraceTimeline";
import "../Monitor/monitor.css";
import "./audit.css";

const AuditTrace = () => {
  const { traceId, orgId } = useParams();
  const navigate = useNavigate();
  const unknown = isUnknownTrace(traceId);

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(!unknown);
  const [notFound, setNotFound] = useState(false);
  const { activeColumns: subjectColumns } = useSubjectConfig(detail?.header?.app_id, detail?.header?.wf_id);

  useEffect(() => {
    if (unknown) return;
    let cancelled = false;
    const fetchTrace = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const response = await getAuditTrace(traceId);
        if (!cancelled) setDetail(response?.data || null);
      } catch (error) {
        console.error("Failed to fetch trace:", error);
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchTrace();
    return () => {
      cancelled = true;
    };
  }, [traceId, unknown]);

  const handleCopyReport = async () => {
    try {
      await navigator.clipboard.writeText(buildReportText(detail?.header, detail?.events, detail?.screen_open));
      toast.success("Report copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy report");
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  const backToList = () => navigate(buildPath.orgAudit({ orgId }));

  const renderBody = () => {
    if (unknown) {
      return (
        <div className="audit_header_card">
          <div>
            <div className="audit_info_label">Unlinked events group</div>
            <p className="font_size_small mt_1">
              This is the catch-all group for audit events without a trace id — typically standalone validation failures
              where the runtime never assigned a trace. It can contain thousands of events and cannot be opened as a
              single timeline. Use the performs list filters (status, user, date) to inspect these rows instead.
            </p>
            <div className="mt_2 w_25">
              <Button variant="primary" onClick={backToList}>
                Back to Audit
              </Button>
            </div>
          </div>
        </div>
      );
    }
    if (loading) {
      return <div className="p_3 color_secondary">Loading trace…</div>;
    }
    if (notFound || !detail?.header) {
      return (
        <div className="audit_header_card">
          <div>
            <div className="audit_info_label">Trace not found</div>
            <p className="font_size_small mt_1">
              No audit data exists for trace <span className="audit_mono">{traceId}</span>. It may not have been
              exported yet — check Audit Exports.
            </p>
            <div className="mt_2 w_25">
              <Button variant="primary" onClick={backToList}>
                Back to Audit
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <>
        <TraceHeaderCard header={detail.header} subjectColumns={subjectColumns} />
        <TraceTimeline events={detail.events || []} screenOpen={detail.screen_open} />
      </>
    );
  };

  return (
    <div className="application_container">
      <AppLeftSideBar type="app" />
      <div className="h_100 d_flex flex_column gap_2 w_100 p_3 overflow_auto">
        <div className="w_100 d_flex justify_between align_center">
          <div className="d_flex align_center gap_1">
            <span className="cursor_pointer d_flex align_center" onClick={backToList} title="Back to Audit">
              <MaterialIcon name="chevron_left" />
            </span>
            <h2>
              Trace <span className="audit_mono">{traceId?.slice(0, 12)}…</span>
            </h2>
          </div>
          {!unknown && detail?.header && (
            <div className="d_flex gap_2">
              <Button variant="secondary" onClick={handleCopyLink} title="Copy a shareable link to this trace">
                Copy link
              </Button>
              <Button variant="primary" onClick={handleCopyReport} title="Copy a plain-text report for tickets">
                Copy as report
              </Button>
            </div>
          )}
        </div>
        {renderBody()}
      </div>
    </div>
  );
};

export default AuditTrace;
